import { faUser } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, IconButton, Menu, MenuItem, Tooltip, Typography } from "@mui/material";
import { Link, routes } from "@cedarjs/router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "src/auth";
import { useStateStore, useUserPreferencesStore, useConfigurationStore } from "src/lib/store";
import RoleOverride from "src/components/RoleOverride/RoleOverride";
import LanguageSwap from "../LanguageSwap/LanguageSwap";
import DarkModeSwap from "../DarkModeSwap/DarkModeSwap";
import PositiveModeSwap from "../PositiveModeSwap/PositiveModeSwap";
import { AnimatePresence } from "framer-motion";
import AnimatedSlidingBlock from "../AnimatedSlidingBlock/AnimatedSlidingBlock";

//https://mui.com/material-ui/react-menu/
const AuthenticatedUserMenu = () => {
  const { logOut, currentUser, hasRole } = useAuth()
  const { t } = useTranslation();
  const site = useStateStore(state => state.site)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { roleOverride } = useConfigurationStore();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  // Get effective roles for display
  const getEffectiveRoles = () => {
    if (currentUser?.isSuperAdmin && roleOverride.selectedRoles.length > 0) {
      return roleOverride.selectedRoles;
    }
    return currentUser?.roles?.filter(role => site?.id ? (role.idSite == site?.id) : true) || [];
  };

  const resetAllDashboardStates = useUserPreferencesStore(state => state.resetAllDashboardStates);
  return (
    <div className="flex flex-col items-end px-0 sm:px-6 ">

      <Button
        id="basic-button"
        aria-controls={open ? 'user-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        className="sm:flex hidden flex-col items-end justify-end  text-right"
      >
        <Typography variant="h4" className="dark:text-white/80 text-black pb-1">{currentUser?.fullName as string}</Typography>
        <Typography variant="caption" className="gap-1 flex h-4">
          <AnimatePresence mode="wait">
            {currentUser?.isSuperAdmin && roleOverride.selectedRoles.length === 0 ?
              <AnimatedSlidingBlock key="header-super-admin" >
                <span className="text-blue-500 font-bold">{t("labels.isSuperAdmin")}</span>
              </AnimatedSlidingBlock>
              : getEffectiveRoles().map((role) =>
                <AnimatedSlidingBlock key={role.name || role.id}>
                  <Tooltip title={role.description || ''} className={roleOverride.selectedRoles.length > 0 ? "text-orange-500 font-bold" : ""} arrow>
                    <span>{role.name}</span>
                  </Tooltip>
                </AnimatedSlidingBlock>
              )}
          </AnimatePresence>
        </Typography>
      </Button>

      <IconButton className="sm:hidden block"
        aria-controls={open ? 'user-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
      >
        <FontAwesomeIcon icon={faUser} className="text-blue-500" />
      </IconButton>
      <Menu
        id="user-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
      >
        {hasRole("ExcelConnect.read") && (
          <Link to={routes.excelConnect()} className="no-underline dark:text-white/80 text-black"><MenuItem onClick={handleClose}>{t("labels.excelConnect")}</MenuItem></Link>
        )}
        {hasRole("AdminPage.read") && (
          <Link to={routes.admin()} className="no-underline dark:text-white/80 text-black"><MenuItem onClick={handleClose}>{t("titles.admin")}</MenuItem></Link>
        )}
        {hasRole("InputLogPage.read") && (
          <Link to={routes.inputLog()} className="no-underline dark:text-white/80 text-black"><MenuItem onClick={handleClose}>{t("titles.inputLog")}</MenuItem></Link>
        )}
        {hasRole("AdminChangeDataCapture.read") && (
          <Link to={routes.adminChangeDataCaptures()} className="no-underline dark:text-white/80 text-black"><MenuItem onClick={handleClose}>{t("titles.changeDataCaptures")}</MenuItem></Link>
        )}
        {hasRole("AlertMonitoringPage.read") && (
          <Link to={routes.alertMonitoring()} className="no-underline dark:text-white/80 text-black"><MenuItem onClick={handleClose}>{t("titles.alertMonitoring")}</MenuItem></Link>
        )}
        {hasRole("TresholdsPage.read") && (
          <Link to={routes.tresholds()} className="no-underline dark:text-white/80 text-black"><MenuItem onClick={handleClose}>{t("titles.outOfTresholdsValues")}</MenuItem></Link>
        )}
        {hasRole("IARequestsPage.read") && (
          <Link to={routes.iaRequests()} className="no-underline dark:text-white/80 text-black"><MenuItem onClick={handleClose}>{t("labels.batchProductionAIRequest")}</MenuItem></Link>
        )}
        { currentUser?.isSuperAdmin && (
          <Link to={routes.cacheControl()} className="no-underline dark:text-blue-500 text-blue-800"><MenuItem onClick={handleClose}>{t("titles.cacheControl")}</MenuItem></Link>
        )}

        <MenuItem><LanguageSwap /></MenuItem>
        <MenuItem><PositiveModeSwap /></MenuItem>
        <MenuItem><DarkModeSwap /></MenuItem>
        <RoleOverride className="mx-4"/>
        <MenuItem onClick={() => { resetAllDashboardStates(); localStorage.clear(); sessionStorage.clear(); }} className="text-orange-500">{t('actions.wipeLocalStorage')}</MenuItem>
        <MenuItem onClick={logOut} className="text-red-500">{t('actions.logOut')}</MenuItem>
      </Menu>
    </div>
  )
}

export default AuthenticatedUserMenu
